import React, { ReactNode } from 'react';
import { styled } from '@linaria/react';
import { Button } from '@daedalus/atlas/Button';
import { Divider } from '@daedalus/atlas/Divider';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Icon } from '@daedalus/atlas/Icon';
import { Popover, PopoverPlacement } from '@daedalus/atlas/Popover';
import { Text } from '@daedalus/atlas/Text';
import { Tooltip, TooltipPlacement } from '@daedalus/atlas/Tooltip';
import { scrollTo } from '@daedalus/core/src/_web/utils/browser';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { MAX_NUMBER_OF_ROOMS } from '@daedalus/core/src/room/business/roomConfiguration/config';
import { RoomConfigType, RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { GuestPicker, GuestPickerButtonMessage } from '../../GuestPicker';
import { useGuestRoomConfig } from '../hooks/useGuestRoomConfig';
import { InfoMessage } from '../InfoMessage';
import { OptimizedRoomGuestPicker } from '../OptimizedRoomGuestPicker';
import { RoomConfigSelector } from '../RoomConfigSelector';
export type Props = {
  roomConfigType?: RoomConfigType;
  isOpen: boolean;
  roomsSplit: RoomSplitType[];
  submitButtonMessage?: ReactNode;
  onSubmit: (roomsSplit: RoomSplitType[]) => void;
  onClose: () => void;
  onRoomsChange: (roomsSplit: RoomSplitType[]) => void;
  onOptimizeRoomsChange: (isOptimizeRooms: boolean) => void;
  onInfoOpen?: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  verticalOffset?: number;
  horizontalOffset?: number;
  placement?: PopoverPlacement;
  onGuestConfigOptionClick?: (optionSelected: RoomConfigType) => void;
};
const _exp = /*#__PURE__*/() => ContentWrapper;
const PopoverContent = /*#__PURE__*/styled(_exp())({
  name: "PopoverContent",
  class: "p6gxt5c",
  propsAsIs: true
});
const _exp3 = /*#__PURE__*/() => Grid;
const RoomConfigurationGrid = /*#__PURE__*/styled(_exp3())({
  name: "RoomConfigurationGrid",
  class: "r12bdixi",
  propsAsIs: true
});
const GuestPickerWrapper = /*#__PURE__*/styled('div')({
  name: "GuestPickerWrapper",
  class: "giuli6o",
  propsAsIs: false
});
const ButtonsWrapper = /*#__PURE__*/styled('div')({
  name: "ButtonsWrapper",
  class: "btdxsiy",
  propsAsIs: false
});
const AddButtonsWrapper = /*#__PURE__*/styled('div')({
  name: "AddButtonsWrapper",
  class: "al58ent",
  propsAsIs: false
});
const ConfigureRoomsTitleWrapper = /*#__PURE__*/styled('div')({
  name: "ConfigureRoomsTitleWrapper",
  class: "csuctpo",
  propsAsIs: false
});
const _exp11 = /*#__PURE__*/() => Tooltip;
const StyledTooltip = /*#__PURE__*/styled(_exp11())({
  name: "StyledTooltip",
  class: "sqdic0v",
  propsAsIs: true
});
const ConfigureRoomsTitle = ({
  onInfoOpen
}: {
  onInfoOpen?: () => void;
}) => <ConfigureRoomsTitleWrapper>
    <Grid container spacing="s000" gap="s200" alignItems="center">
      <Text variant="titleM">
        <FormattedMessageWrapper dataId="configureRooms" id="configureRooms" defaultMessage="Configure rooms" />
      </Text>
      <StyledTooltip placement={TooltipPlacement.BottomStart} content={<InfoMessage />} onRender={onInfoOpen}>
        <Icon name="Information" />
      </StyledTooltip>
    </Grid>
  </ConfigureRoomsTitleWrapper>;
export const GuestRoomConfigPopover = ({
  anchorRef,
  isOpen,
  roomsSplit,
  submitButtonMessage,
  onSubmit,
  onClose,
  onRoomsChange,
  onOptimizeRoomsChange,
  onInfoOpen,
  onGuestConfigOptionClick,
  roomConfigType = RoomConfigType.Optimized,
  verticalOffset,
  horizontalOffset,
  placement = PopoverPlacement.Right
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    roomConfigIsOptimized,
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    localRoomsSplit,
    handleSubmit,
    handleAddRoom,
    updateRoomsSplit
  } = useGuestRoomConfig({
    roomConfigType,
    roomsSplit,
    onSubmit,
    onRoomsChange
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToBottom = () => {
    if (!containerRef.current) return;
    scrollTo(containerRef.current, containerRef.current.scrollHeight, 1000);
  };
  const handleAddNewRoom = React.useCallback(() => {
    handleAddRoom();
    scrollToBottom();
  }, [handleAddRoom, scrollToBottom]);
  return <Popover isOpen={isOpen} placement={placement} anchorRef={anchorRef} verticalOffset={verticalOffset} horizontalOffset={horizontalOffset} onOutsideClick={onClose}>
      <PopoverContent>
        <RoomConfigurationGrid container spacing="s000" gap="s300" direction="column">
          <ConfigureRoomsTitle onInfoOpen={onInfoOpen} />
          <RoomConfigSelector isOptimized={roomConfigIsOptimized} onSelectionClick={onGuestConfigOptionClick} onChange={onOptimizeRoomsChange} size="md" />
        </RoomConfigurationGrid>
        <Divider sideMargin="s500" />
        <GuestPickerWrapper ref={containerRef}>
          {roomConfigIsOptimized ? <OptimizedRoomGuestPicker isCompactLayout customTitle={<FormattedMessageWrapper dataId="addGuests" id="addGuests" defaultMessage="Add guests" />} roomsSplit={localRoomsSplit} updateRoomsSplit={updateRoomsSplit} /> : <GuestPicker isCompactLayout roomsSplit={localRoomsSplit} updateRoomsSplit={updateRoomsSplit} />}
        </GuestPickerWrapper>
      </PopoverContent>

      <ButtonsWrapper>
        {!roomConfigIsOptimized && roomsSplit.length < MAX_NUMBER_OF_ROOMS && <AddButtonsWrapper>
            <Button dataId="AddRoomButton" variant="secondary" onClick={handleAddNewRoom} size="lg" fullWidth iconStart={<Icon name="Plus" />}>
              <FormattedMessageWrapper id="addRoom" defaultMessage="Add room" />
            </Button>
          </AddButtonsWrapper>}
        <Button variant="primary" dataId="UpdateRoomsGuestsButton" onClick={() => handleSubmit(localRoomsSplit)} size="xl" fullWidth>
          {submitButtonMessage}
          <> · </>
          <GuestPickerButtonMessage numberOfGuests={numberOfAdults + numberOfChildren} numberOfRooms={roomConfigIsOptimized ? undefined : numberOfRooms} />
        </Button>
      </ButtonsWrapper>
    </Popover>;
};

require("./GuestRoomConfigPopover.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./GuestRoomConfigPopover.tsx");